import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { cellClass, CustomerFilter, DateField, EmptyRow, FilterGrid, Pager, RangeError, ReportCard, ReportPage, ReportStatus, ReportTable, SelectField, SummaryTiles, useCustomerOptions, useReportData, useReportParams } from './ReportParts';
import { dateParam, dateRangeError, pageParam, paymentMethodLabel, REPORT_PAGE_SIZE, reportDate, reportMoney } from './reportUi';

const METHODS = Object.values(types.PaymentMethod);
const METHOD_COLUMNS = [{ label: 'Method' }, { label: 'Payments', numeric: true }, { label: 'Total', numeric: true }];
const PAYMENT_COLUMNS = [{ label: 'Date' }, { label: 'Customer' }, { label: 'Method' }, { label: 'Reference' }, { label: 'Amount', numeric: true }];

export function PaymentReportPage() {
  const api = useMemo(() => createAPIClient(), []);
  const customerOptions = useCustomerOptions(api);
  const { params, change } = useReportParams();
  const startDate = dateParam(params.get('startDate'));
  const endDate = dateParam(params.get('endDate'));
  const customerId = params.get('customerId') || '';
  const method = params.get('method') || '';
  const page = pageParam(params.get('page'));
  const rangeError = dateRangeError(startDate, endDate);
  const query = { startDate, endDate, customerId, method, page, limit: REPORT_PAGE_SIZE };
  const state = useReportData(
    rangeError ? null : JSON.stringify(query),
    () => api.getPaymentReport(query),
    'Could not load the payment report',
  );

  return (
    <ReportPage title="Payments" intro="ACTIVE payments by payment date (voided payments are excluded), with totals by method.">
      <FilterGrid>
        <DateField id="payments-start" label="Start date" value={startDate} onChange={(value) => change({ startDate: value })} />
        <DateField id="payments-end" label="End date" value={endDate} onChange={(value) => change({ endDate: value })} />
        <CustomerFilter id="payments-customer" value={customerId} onChange={(value) => change({ customerId: value })} options={customerOptions} />
        <SelectField id="payments-method" label="Method" value={method} onChange={(value) => change({ method: value })}>
          <option value="">All methods</option>
          {METHODS.map((option) => <option key={option} value={option}>{paymentMethodLabel(option)}</option>)}
        </SelectField>
      </FilterGrid>
      <RangeError message={rangeError} />
      <ReportStatus state={state} label="payment report">
        {(report) => (
          <>
            <SummaryTiles tiles={[
              { label: 'Total received', value: reportMoney(report.totalPayments) },
              { label: 'Payments', value: report.pagination.total },
            ]} />
            <ReportCard title="By method">
              <ReportTable columns={METHOD_COLUMNS} caption="Payments by method">
                {report.byMethod.length === 0 ? <EmptyRow colSpan={METHOD_COLUMNS.length} message="No payments match these filters." /> : report.byMethod.map((row) => (
                  <tr key={row.method}>
                    <td className={cellClass()} data-label="Method" data-primary>{paymentMethodLabel(row.method)}</td>
                    <td className={cellClass(true)} data-label="Payments">{row.count}</td>
                    <td className={cellClass(true)} data-label="Total">{reportMoney(row.total)}</td>
                  </tr>
                ))}
              </ReportTable>
            </ReportCard>
            <ReportCard title="Payment list">
              <ReportTable columns={PAYMENT_COLUMNS} caption="Payments">
                {report.data.length === 0 ? <EmptyRow colSpan={PAYMENT_COLUMNS.length} message="No payments match these filters." /> : report.data.map((payment: types.PaymentReportRow) => (
                  <tr key={payment.id}>
                    <td className={cellClass()} data-label="Date" data-primary><Link to={`/payments/${payment.id}`} className="text-blue-700 hover:underline">{reportDate(payment.paymentDate)}</Link></td>
                    <td className={cellClass()} data-label="Customer"><Link to={`/customers/${payment.customerId}`} className="text-blue-700 hover:underline break-words">{payment.customerName}</Link></td>
                    <td className={cellClass()} data-label="Method">{paymentMethodLabel(payment.paymentMethod)}</td>
                    <td className={`${cellClass()} break-words`} data-label="Reference">{payment.referenceNumber || '—'}</td>
                    <td className={cellClass(true)} data-label="Amount">{reportMoney(payment.amount)}</td>
                  </tr>
                ))}
              </ReportTable>
              <Pager pagination={report.pagination} noun="payment" onPage={(next) => change({ page: next > 1 ? String(next) : '' })} />
            </ReportCard>
          </>
        )}
      </ReportStatus>
    </ReportPage>
  );
}
